/**
 * Inputs: booking + existing payments + a credit amount.
 * Outputs: remaining household shares and a suggested funding split for this payment.
 * Never invents a 50/50 split when the reservation belongs to one household.
 */

import type { Allocation, Booking, SupplierPayment, SupplierRefund } from "./schema.ts";
import { bookingLedger, householdSupplierRemaining } from "./ledger.ts";

export function remainingHouseholdAmounts(
  booking: Booking,
  payments: SupplierPayment[],
  refunds: SupplierRefund[],
  householdIds: string[],
): Record<string, number> {
  const ledger = bookingLedger(booking, payments, refunds, householdIds);
  const remaining: Record<string, number> = {};
  for (const id of householdIds) {
    remaining[id] = householdSupplierRemaining(ledger, id);
  }
  if (ledger.unallocated && booking.cost) {
    remaining.unallocated = Math.max(0, booking.cost.amountMinor - (Object.values(ledger.funded).reduce((s, n) => s + n, 0)));
  }
  return remaining;
}

export function defaultFundedAllocations(
  booking: Booking,
  amountMinor: number,
  payments: SupplierPayment[],
  refunds: SupplierRefund[],
  householdIds: string[],
): Allocation[] {
  if (!booking.cost || amountMinor <= 0) return [];
  const remaining = remainingHouseholdAmounts(booking, payments, refunds, householdIds);
  const rows = householdIds
    .map((householdId) => ({ householdId, remaining: remaining[householdId] ?? 0 }))
    .filter((row) => row.remaining > 0);
  const pool = rows.reduce((sum, row) => sum + row.remaining, 0);
  if (rows.length === 0 || pool <= 0) {
    if (booking.householdId) return [{ householdId: booking.householdId, amountMinor }];
    return [];
  }
  if (rows.length === 1) {
    return [{ householdId: rows[0].householdId, amountMinor }];
  }
  let used = 0;
  return rows.map((row, index) => {
    const share = index === rows.length - 1
      ? amountMinor - used
      : Math.trunc((amountMinor * row.remaining) / pool);
    used += share;
    return { householdId: row.householdId, amountMinor: share };
  });
}

export function fundingPreviewText(input: {
  payerLabel: string;
  householdLabels: Record<string, string>;
  funded: Allocation[];
  claims: Array<{ fromHouseholdId: string; toHouseholdId: string; currency: string; amountMinor: number }>;
  format: (currency: string, amountMinor: number) => string;
  currency: string;
}): string {
  if (input.funded.length === 0) return "Choose which household shares this credit covers.";
  const own = input.funded.filter((row) => input.householdLabels[row.householdId]);
  const parts = own.map((row) => `${input.householdLabels[row.householdId]} share ${input.format(input.currency, row.amountMinor)}`);
  if (input.claims.length === 0) {
    return `${input.payerLabel} is covering ${parts.join(" and ")}. No reimbursement between households from this payment.`;
  }
  const claimBits = input.claims.map((c) =>
    `${input.householdLabels[c.fromHouseholdId] ?? c.fromHouseholdId} would owe ${input.householdLabels[c.toHouseholdId] ?? c.toHouseholdId} ${input.format(c.currency, c.amountMinor)}`,
  );
  return `${input.payerLabel} is covering ${parts.join(" and ")}. ${claimBits.join(". ")}.`;
}
